import { useLayoutEffect, useState } from 'react'

export type AgTechTourTargetRect = { top: number; left: number; width: number; height: number }

// Tracks the on-screen rectangle of a tour target for as long as the step is showing. Targets are
// often created after the step activates (a dialog moving to another page, a popup opening, data
// still loading) and can move afterwards, so a one-time measurement would highlight nothing or the
// wrong area. Returns null while the target is not in the DOM.
export const useAgTechTourTargetRect = (targetId: string): AgTechTourTargetRect | null => {
    const [rect, setRect] = useState<AgTechTourTargetRect | null>(null)

    useLayoutEffect(() => {
        let frame = 0
        let hasScrolledIntoView = false

        const measure = () => {
            const el = document.getElementById(targetId)

            if (el) {
                if (!hasScrolledIntoView) {
                    hasScrolledIntoView = true
                    el.scrollIntoView({ block: 'nearest', inline: 'nearest' })
                }

                const r = el.getBoundingClientRect()
                setRect(prev => prev && prev.top === r.top && prev.left === r.left && prev.width === r.width && prev.height === r.height
                    ? prev
                    : { top: r.top, left: r.left, width: r.width, height: r.height })
            } else {
                hasScrolledIntoView = false
                setRect(prev => prev ? null : prev)
            }

            frame = requestAnimationFrame(measure)
        }

        measure()

        return () => cancelAnimationFrame(frame)
    }, [targetId])

    return rect
}
